import React, { useRef, useCallback, useEffect } from "react";
import { gsap } from "gsap";
import clsx from "clsx";

import { capitalizeString } from "@excalidraw/common";

import { trackEvent } from "../analytics";
import { isHandToolActive } from "../appState";
import { t } from "../i18n";

import { useTunnels } from "../context/tunnels";

import { SHAPES } from "./shapes";
import { frameToolIcon, handIcon, PenModeIcon, CirclePlusIcon } from "./icons";

import "./LofiAppToolbar.scss";

import type { AppClassProperties, AppProps, UIAppState } from "../types";

interface LofiAppToolbarProps {
  appState: UIAppState;
  app: AppClassProperties;
  UIOptions: AppProps["UIOptions"];
  onPenModeToggle: (force: boolean | null) => void;
  onLibraryToggle?: (open: boolean) => void;
  isLibraryOpen?: boolean;
  renderWelcomeScreen?: boolean;
}

interface ToolItem {
  id: string;
  icon: React.ReactNode;
  label: string;
  checked?: boolean;
  onClick: () => void;
  keyBinding?: string;
  testId?: string;
  className?: string;
}

// Config for the wave effect - matching the sample
const config = {
  swatches: 12,
  threshold: 30,
  start: 66,
  distance: 12,
  rotation: -5,
  out: "power2.out",
  in: "power4.in",
};

export const LofiAppToolbar: React.FC<LofiAppToolbarProps> = ({
  appState,
  app,
  UIOptions,
  onPenModeToggle,
  onLibraryToggle,
  isLibraryOpen = false,
  renderWelcomeScreen,
}) => {
  const tunnels = useTunnels();
  const toolbarRef = useRef<HTMLDivElement>(null);
  const settersRef = useRef<
    Array<(data: { distance: number; width: number }) => void>
  >([]);
  const easesRef = useRef({
    in: gsap.parseEase(config.in),
    out: gsap.parseEase(config.out),
  });

  // Initialize setters for each tool button
  useEffect(() => {
    const toolbar = toolbarRef.current;
    if (!toolbar) {
      return;
    }

    const tools = toolbar.querySelectorAll(".lofi-toolbar-card");
    settersRef.current = [];

    tools.forEach((tool, index) => {
      // Create GSAP setter for this tool using the wave effect logic
      const setter = (data: { distance: number; width: number }) => {
        const clamped = gsap.utils.clamp(
          -config.threshold,
          config.threshold,
          data.distance + data.width * 0.5,
        );

        const mapped = gsap.utils.mapRange(
          -config.threshold,
          config.threshold,
          -1,
          1,
        )(clamped);

        const active = Math.abs(clamped) <= data.width * 0.4;
        const offset = active ? 0 : 0;

        const power =
          mapped > 0
            ? 1 - easesRef.current.in(mapped) - offset
            : 1 - easesRef.current.out(Math.abs(mapped)) - offset;

        gsap.set(tool, { "--power": power });
      };

      settersRef.current[index] = setter;
    });
  }, []);

  // Reinitialize setters when toolbar updates
  useEffect(() => {
    const timer = setTimeout(() => {
      const toolbar = toolbarRef.current;
      if (!toolbar) {
        return;
      }

      const tools = toolbar.querySelectorAll(".lofi-toolbar-card");
      if (tools.length !== settersRef.current.length) {
        settersRef.current = [];
        tools.forEach((tool, index) => {
          const setter = (data: { distance: number; width: number }) => {
            const clamped = gsap.utils.clamp(
              -config.threshold,
              config.threshold,
              data.distance + data.width * 0.5,
            );

            const mapped = gsap.utils.mapRange(
              -config.threshold,
              config.threshold,
              -1,
              1,
            )(clamped);

            const active = Math.abs(clamped) <= data.width * 0.4;
            const offset = active ? 0 : 0;

            const power =
              mapped > 0
                ? 1 - easesRef.current.in(mapped) - offset
                : 1 - easesRef.current.out(Math.abs(mapped)) - offset;

            gsap.set(tool, { "--power": power });
          };

          settersRef.current[index] = setter;
        });
      }
    }, 100);

    return () => clearTimeout(timer);
  });

  const syncWave = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const toolbar = toolbarRef.current;
    if (!toolbar) {
      return;
    }

    const x = event.clientX;
    const tools = toolbar.querySelectorAll(".lofi-toolbar-card");

    // Apply wave effect to each tool based on distance from cursor
    tools.forEach((tool, i) => {
      const setter = settersRef.current[i];
      if (!setter) {
        return;
      }

      const { left, width } = tool.getBoundingClientRect();

      setter({
        distance: x - (left + width * 0.5),
        width,
      });
    });
  }, []);

  const settleWave = useCallback(() => {
    // Reset all cards to normal state
    const toolbar = toolbarRef.current;
    if (!toolbar) {
      return;
    }

    const tools = toolbar.querySelectorAll(".lofi-toolbar-card");
    tools.forEach((tool) => {
      gsap.set(tool, { "--power": 0 });
    });
  }, []);

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      // Only apply wave effect on desktop (non-touch devices)
      if (window.innerWidth > 768) {
        syncWave(event);
      }
    },
    [syncWave],
  );

  const handlePointerLeave = useCallback(() => {
    // Only apply wave effect on desktop (non-touch devices)
    if (window.innerWidth > 768) {
      settleWave();
    }
  }, [settleWave]);

  // Custom label mapping
  const getCustomLabel = (id: string, defaultLabel: string): string => {
    const customLabels: Record<string, string> = {
      hand: "Pan",
      selection: "Select",
      image: "Image",
      frame: "Frame",
    };
    return customLabels[id] || defaultLabel;
  };

  // Create tool items array
  const baseToolItems: ToolItem[] = [
    // Only show pen mode if pen is detected
    ...(appState.penDetected
      ? [
          {
            id: "pen-mode",
            icon: PenModeIcon,
            label: t("toolBar.penMode"),
            checked: appState.penMode,
            onClick: () => onPenModeToggle(null),
            testId: "pen-mode",
          },
        ]
      : []),
    // Library button
    ...(onLibraryToggle
      ? [
          {
            id: "library",
            icon: CirclePlusIcon,
            label: "Assets",
            checked: isLibraryOpen,
            onClick: () => {
              trackEvent("toolbar", "library", "floating-bottom");

              if (isLibraryOpen) {
                // When closing library, keep current tool active
                // No need to change tool
              } else {
                // When opening library, hide welcome screen by triggering toolbar interaction
                // Use setActiveTool with current selection tool and fromToolbar=true
                app.setActiveTool({ type: "selection" }, true, true);
              }

              // Toggle the library state
              onLibraryToggle(!isLibraryOpen);
            },
            testId: "floating-toolbar-library",
          },
        ]
      : []),
    {
      id: "hand",
      icon: handIcon,
      label: getCustomLabel("hand", t("toolBar.hand")),
      checked: isHandToolActive(appState),
      onClick: () => {
        if (!isHandToolActive(appState)) {
          trackEvent("toolbar", "hand", "floating-bottom");
        }
        app.setActiveTool({ type: "hand" }, false, true);
      },
      keyBinding: "H",
      testId: "floating-toolbar-hand",
    },
  ];

  const shapeItems: ToolItem[] = [];
  SHAPES.slice(0, -1).forEach(({ value, icon, key, numericKey, fillable }) => {
    if (
      UIOptions.tools?.[
        value as Extract<typeof value, keyof AppProps["UIOptions"]["tools"]>
      ] === false
    ) {
      return;
    }

    // Hide diamond tool
    if (value === "diamond") {
      return;
    }

    const label = t(`toolBar.${value}`);
    const letter =
      key && capitalizeString(typeof key === "string" ? key : key[0]);

    shapeItems.push({
      id: value,
      icon,
      label: getCustomLabel(value, capitalizeString(label)),
      checked: appState.activeTool.type === value,
      onClick: () => {
        if (appState.activeTool.type !== value) {
          trackEvent("toolbar", value, "floating-bottom");
        }
        app.setActiveTool({ type: value }, false, true);
      },
      keyBinding: letter || numericKey,
      testId: `floating-toolbar-${value}`,
      className: clsx("Shape", { fillable }),
    });
  });

  const frameItem: ToolItem = {
    id: "frame",
    icon: frameToolIcon,
    label: getCustomLabel("frame", capitalizeString(t("toolBar.frame"))),
    checked: appState.activeTool.type === "frame",
    onClick: () => {
      if (appState.activeTool.type !== "frame") {
        trackEvent("toolbar", "frame", "floating-bottom");
      }
      app.setActiveTool({ type: "frame" }, false, true);
    },
    keyBinding: "F",
    testId: "floating-toolbar-frame",
    className: "Shape",
  };

  const toolItems = [...baseToolItems, ...shapeItems, frameItem];

  return (
    <div className="lofi-app-toolbar">
      <div
        className="lofi-toolbar-container"
        ref={toolbarRef}
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
        style={
          {
            "--distance": config.distance,
            "--rotate": config.rotation,
            "--threshold": config.threshold,
            "--start": config.start,
          } as React.CSSProperties
        }
      >
        <ul
          className="lofi-toolbar-stack"
          style={
            {
              "--item-count": toolItems.length,
            } as React.CSSProperties
          }
        >
          {toolItems.map((item, index) => (
            <li
              key={item.id}
              className="lofi-toolbar-item"
              style={
                {
                  "--i": index,
                  "--delay": `${
                    Math.sin(((index / toolItems.length) * Math.PI) / 4) * 1
                  }s`,
                } as React.CSSProperties
              }
            >
              <button
                className={clsx("lofi-toolbar-card", item.className, {
                  "lofi-toolbar-card--active": item.checked,
                })}
                onClick={item.onClick}
                title={`${item.label}${
                  item.keyBinding ? ` — ${item.keyBinding}` : ""
                }`}
                aria-label={item.label}
                data-testid={item.testId}
              >
                <div className="lofi-toolbar-card__icon">
                  {item.icon}
                  {item.keyBinding && (
                    <span className="lofi-toolbar-card__keybinding">
                      {item.keyBinding}
                    </span>
                  )}
                </div>
                <div className="lofi-toolbar-card__label">{item.label}</div>
              </button>
              {renderWelcomeScreen && item.id === "library" && (
                <tunnels.WelcomeScreenAssetsHintTunnel.Out />
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
